// Anggota project, from a PBJ project's header menu (API GET/DELETE
// /pbj/projects/:id/members, migration 0037): the owner and the accounts the
// stage-0 documents were sent to (Kirim dokumen tahap 0, ./Handover.tsx). The
// owner removes a member; a member leaves (then the project is no longer
// theirs to open, so the page goes to the PBJ project list). Each removal is
// asked once in its own row.
import { useEffect, useState } from "react";
import * as API from "../api";
import type { PbjMembers } from "../api";
import { useUser } from "../auth";
import { formatDate } from "../lib/format";
import { Badge, Dialog } from "../ui/components";
import { Glyph } from "../ui/icons";

export function MembersDialog({ projectId, open, onClose, onLeft }: { projectId: string; open: boolean; onClose: () => void; onLeft: () => void }) {
  const me = useUser();
  const [members, setMembers] = useState<PbjMembers | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoadError(null);
    setError(null);
    setAsking(null);
    API.getPbjMembers(projectId).then(
      (m) => live && setMembers(m),
      (err) => live && setLoadError(API.errorMessage(err)),
    );
    return () => {
      live = false;
    };
  }, [open, projectId]);

  const remove = async (userId: string) => {
    setBusy(userId);
    setError(null);
    try {
      const next = await API.removePbjMember(projectId, userId);
      setAsking(null);
      if (userId === me.id || next === null) {
        onLeft();
        return;
      }
      setMembers(next);
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} title="Anggota project" description="Pemilik project dan akun yang menerima dokumen tahap Rencana Kerja & Anggaran." onClose={onClose} busy={busy !== null}>
      {loadError ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>Anggota belum dapat dimuat. {loadError}</span>
        </p>
      ) : !members ? (
        <p className="pbj-next" role="status">
          Memuat anggota
        </p>
      ) : (
        <ul className="rows pbj-members" aria-label="Anggota project">
          <li className="row">
            <span className="row-main">
              <span className="row-title">
                {members.owner.name}
                {members.owner.userId === me.id ? " (Anda)" : ""}
              </span>
              <span className="row-meta">Pembuat project</span>
            </span>
            <span className="row-side">
              <Badge tone="info">Pemilik</Badge>
            </span>
          </li>
          {members.members.map((m) => {
            const self = m.userId === me.id;
            const can = members.canManage || self;
            const label = self ? "Keluar dari project" : "Keluarkan";
            return (
              <li key={m.userId} className="row">
                <span className="row-main">
                  <span className="row-title">
                    {m.name}
                    {self ? " (Anda)" : ""}
                  </span>
                  <span className="row-meta">Anggota sejak {formatDate(m.createdAt)}</span>
                  {asking === m.userId ? (
                    <span className="row-meta" role="status">
                      {self ? "Anda tidak dapat membuka project ini lagi setelah keluar." : `${m.name} tidak dapat membuka project ini lagi.`}
                    </span>
                  ) : null}
                </span>
                {can ? (
                  <span className="row-side pbj-member-actions">
                    {asking === m.userId ? (
                      <>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy !== null} onClick={() => setAsking(null)}>
                          Batal
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          data-tone="danger"
                          disabled={busy !== null}
                          aria-busy={busy === m.userId || undefined}
                          onClick={() => void remove(m.userId)}
                        >
                          {busy === m.userId ? "Memproses" : `Ya, ${label.toLowerCase()}`}
                        </button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busy !== null} aria-label={`${label}: ${m.name}`} onClick={() => setAsking(m.userId)}>
                        {label}
                      </button>
                    )}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {members && members.members.length === 0 ? <p className="pbj-next">Belum ada anggota lain. Anggota bertambah saat dokumen tahap Rencana Kerja & Anggaran dikirim ke PPK.</p> : null}
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </Dialog>
  );
}
